import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  TextInput,
  Textarea,
  Paper,
  Box,
  SimpleGrid,
  Checkbox,
  Accordion,
  Divider,
  ThemeIcon,
  Alert,
  Badge,
  Radio,
  Loader,
  Center,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { toDateValue } from '../../../../utils/dateHelpers';
import { Users, User, Layers, ArrowLeft, Send, Mail, AlertTriangle, Edit, DollarSign } from 'react-feather';
import { useInsertAtCursor } from '../../../../hooks';
import { MergeTagChips } from '../sessions/feedback';
import type { CohortWithDetails, TimeSlotResponse, CompensationCalculationResponse } from '../../../../types';
import {
  cohortCoachesApi,
  type CohortCoachWithDetails,
  type SendAvailabilityRequestPayload,
  type AvailabilitySendPreview,
} from '../../../../services/api/cohortCoaches';
import { cohortCompensationApi } from '../../../../services/api/cohortCompensation';
import { timeSlotsApi } from '../../../../services/api/timeSlots';
import { CohortTimeline } from '../../../../components/CohortTimeline';
import { cohortEventsApi } from '../../../../services/api/cohortEvents';
import type { TimelineEvent } from '../../../../services/api/cohortEvents';
import { formatDateOnlyRange, formatCents } from '../../../../utils/formatters';

interface SendAvailabilityRequestModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  selectedCoachUserIds: string[];
  coaches: CohortCoachWithDetails[];
  onRequestSent: () => void;
  onEditCohortDetails?: () => void;
}

type ModalityType = 'group_coaching' | 'one_on_one' | 'workshop';

/*
 * Style guide tokens. Mantine's `dimmed` resolves to #868E96, which fails AA
 * on both white and the callout background. #666666 is the Medley secondary
 * token and clears 4.5:1 on both.
 */
const MEDLEY_TEXT_SECONDARY = '#666666';
/*
 * #E9A23B is the warning fill and measures about 2.0:1 as text on white, which
 * fails worse than the 3.10:1 defect this pass fixed. #92650E is the style
 * guide's readable amber and clears AA at 5.13:1. It carries the two lines
 * that name the destructive half of a send, so it has to be legible.
 */
const MEDLEY_WARNING_TEXT = '#92650E';

const MODALITY_OPTIONS: { value: ModalityType; label: string; icon: React.ReactNode }[] = [
  { value: 'group_coaching', label: 'Group Coaching', icon: <Users size={20} /> },
  { value: 'one_on_one', label: '1:1 Coaching', icon: <User size={20} /> },
  { value: 'workshop', label: 'Workshop', icon: <Layers size={20} /> },
];

// Merge tags available for email template
const MERGE_TAGS = [
  { tag: '{{coach_name}}', description: 'Coach first name' },
  { tag: '{{start_date}}', description: 'Cohort start date' },
  { tag: '{{end_date}}', description: 'Cohort end date' },
  { tag: '{{cohort_context}}', description: 'Cohort context you provide' },
  { tag: '{{modalities}}', description: 'Selected modalities' },
  { tag: '{{session_count}}', description: 'Number of sessions' },
  { tag: '{{cadence}}', description: 'Session frequency' },
  { tag: '{{availability_link}}', description: 'Response form link' },
  { tag: '{{deadline}}', description: 'Response deadline' },
  { tag: '{{cm_name}}', description: 'Your name' },
];

const DEFAULT_EMAIL_SUBJECT = 'Coaching Opportunity: {{cohort_name}}';
const DEFAULT_EMAIL_MESSAGE = `Hi {{coach_name}},

We're excited to share a new Medley coaching opportunity beginning {{start_date}} and continuing through {{end_date}}.

{{cohort_context}}

Please respond by {{deadline}}.

Thanks as always for your thoughtful and impactful coaching. Let us know if you have any questions!

Best,
{{cm_name}}
Program Delivery, Medley`;

export function SendAvailabilityRequestModal({
  opened,
  onClose,
  cohort,
  selectedCoachUserIds,
  coaches,
  onRequestSent,
  onEditCohortDetails,
}: SendAvailabilityRequestModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Step 1 state
  const [selectedModalities, setSelectedModalities] = useState<ModalityType[]>(['group_coaching']);
  const [timeSlots, setTimeSlots] = useState<TimeSlotResponse[]>([]);
  const [selectedTimeSlotIds, setSelectedTimeSlotIds] = useState<string[]>([]);
  const [responseDeadline, setResponseDeadline] = useState<Date | null>(null);
  const [cohortContext, setCohortContext] = useState('');

  // MED-918 AC17: open-time snapshot of the selection. We compare this
  // against the live `coaches` prop on every render to detect coaches who
  // have been removed from the cohort since the modal opened. If any are
  // detected, an inline pre-flight warning lists the dropped coach names
  // BEFORE Send is clicked (not as a post-send toast — per dev pushback on
  // surfacing the warning after an irreversible action). Pairs with AC14's
  // parent-level `selectedCoaches` reconciliation and AC16's server-side
  // pre-flight filter as defense-in-depth.
  const [openTimeSelectionSnapshot, setOpenTimeSelectionSnapshot] = useState<{
    userIds: string[];
    namesByUserId: Record<string, string>;
  } | null>(null);

  // MDF-233: what a send would do, fetched when the modal opens.
  //
  // Before this change the modal knew nothing about existing requests, so
  // every Send inserted a new row. Coaches ended up with three cards for one
  // ask. Now the modal splits the selection, says so, and defaults to the
  // harmless outcome: a reminder.
  const [preview, setPreview] = useState<AvailabilitySendPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [onExisting, setOnExisting] = useState<'remind' | 'replace'>('remind');

  // Step 2 state
  const [emailSubject, setEmailSubject] = useState(DEFAULT_EMAIL_SUBJECT);
  const [emailMessage, setEmailMessage] = useState(DEFAULT_EMAIL_MESSAGE);
  const [lastFocusedField, setLastFocusedField] = useState<'subject' | 'body'>('body');
  const subjectField = useInsertAtCursor<HTMLInputElement>();
  const bodyField = useInsertAtCursor<HTMLTextAreaElement>();

  const handleInsertTag = useCallback((tag: string) => {
    if (lastFocusedField === 'subject') {
      subjectField.insert(tag, () => emailSubject, setEmailSubject);
    } else {
      bodyField.insert(tag, () => emailMessage, setEmailMessage);
    }
  }, [lastFocusedField, subjectField, bodyField, emailSubject, emailMessage]);

  // Timeline state
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [timelineTotalEvents, setTimelineTotalEvents] = useState(0);

  // MED-663: Compensation data for selected coaches
  const [compData, setCompData] = useState<CompensationCalculationResponse | null>(null);

  // MDF-233: fetch the split, then prefill the form from the open request when
  // every selected coach who has one was asked the same thing.
  //
  // The prefill is what makes "changing nothing sends a reminder" true. The
  // modal's own defaults tick every time slot and derive modalities from coach
  // proficiency, so without this the form would almost never match what was
  // actually asked, and a manager who only wanted to nudge somebody would be
  // told they had changed the ask.
  useEffect(() => {
    if (!opened || !cohort.id || selectedCoachUserIds.length === 0) return;
    setLoadingPreview(true);
    cohortCoachesApi
      .previewAvailabilitySend(cohort.id, selectedCoachUserIds)
      .then(result => {
        setPreview(result);
      })
      .catch(error => {
        console.error('Failed to preview the availability send:', error);
        setPreview(null);
      })
      .finally(() => setLoadingPreview(false));
  }, [opened, cohort.id, selectedCoachUserIds]);

  // Fetch time slots, timeline, and compensation for the cohort
  useEffect(() => {
    if (opened && cohort.id) {
      // MDF-233: the initial slot selection is decided once, below, after the
      // preview lands. Preselecting here as well would race the prefill, and
      // whichever request finished last would win.
      timeSlotsApi.list(cohort.id).then(result => {
        setTimeSlots(result.time_slots);
      });
      cohortEventsApi.getTimeline(cohort.id).then(result => {
        setTimelineEvents(result.events);
        setTimelineTotalEvents(result.total_events);
      });
      // MED-663: Fetch compensation calculations for coach estimates
      cohortCompensationApi.getCalculation(cohort.id).then(setCompData).catch(() => null);
    }
  }, [opened, cohort.id]);

  /**
   * MDF-233: decide the initial form contents exactly once per open, after
   * both the time slots and the preview have landed.
   *
   * When every selected coach who already holds an open request was asked the
   * same thing, the form starts as that ask. Changing nothing then means
   * "remind", which is the outcome a manager nudging somebody expects.
   *
   * Otherwise the form falls back to the old defaults: every time slot ticked,
   * modalities from shared coach proficiency.
   */
  const initialSelectionApplied = useRef(false);
  useEffect(() => {
    if (!opened) {
      initialSelectionApplied.current = false;
      // Reminder is the default because it is the outcome that cannot
      // surprise anybody. Replace must be chosen again every time, or a
      // reopened modal silently marks live requests replaced.
      setOnExisting('remind');
      return;
    }
    if (initialSelectionApplied.current) return;
    if (loadingPreview || timeSlots.length === 0) return;

    const open = preview?.open_requests ?? [];
    const first = open[0];
    const sharedAsk =
      first &&
      open.every(
        r =>
          [...r.requested_time_slots].sort().join('|') ===
            [...first.requested_time_slots].sort().join('|') &&
          [...r.modalities].sort().join('|') === [...first.modalities].sort().join('|'),
      );

    if (sharedAsk) {
      setSelectedTimeSlotIds(first.requested_time_slots);
      setSelectedModalities(first.modalities as ModalityType[]);
      if (first.response_deadline) setResponseDeadline(new Date(first.response_deadline));
    } else {
      setSelectedTimeSlotIds(timeSlots.map(ts => ts.id));
    }
    initialSelectionApplied.current = true;
  }, [opened, loadingPreview, preview, timeSlots]);

  // MED-1155 AC1: the cohort's configured modalities are the HARD gate on what
  // the CM can request. Only these are offered/selectable in the grid below;
  // coach proficiency is a separate SOFT signal that drives a warning, never a
  // gate. Derived from cohort.modalities (populated by cohortsApi.getById).
  const configuredModalityTypes = (cohort.modalities ?? []).map(m => m.modality_type);
  const availableModalityOptions = MODALITY_OPTIONS.filter(opt =>
    configuredModalityTypes.includes(opt.value),
  );

  // Compute pre-selected modalities from coach proficiencies, constrained to
  // the cohort's configured set.
  const getInitialModalities = (): ModalityType[] => {
    const configured = configuredModalityTypes as ModalityType[];
    // Default pre-selection when proficiency can't decide it: prefer
    // group_coaching when the cohort has it, otherwise pre-select nothing and
    // let the CM pick from the configured options shown in the grid.
    const configuredDefault: ModalityType[] = configured.includes('group_coaching')
      ? ['group_coaching']
      : [];

    const selectedCoaches = coaches.filter(c => selectedCoachUserIds.includes(c.user_id));
    if (selectedCoaches.length === 0) return configuredDefault;

    const proficiencyArrays = selectedCoaches
      .map(c => c.modality_proficiencies || [])
      .filter(p => p.length > 0);

    if (proficiencyArrays.length === 0) return configuredDefault;

    // Find modalities ALL selected coaches share
    const commonModalities = proficiencyArrays.reduce<string[]>(
      (common, current) => common.filter(m => current.includes(m)),
      proficiencyArrays[0],
    );

    // MED-1155 AC1: cohort config is the hard gate — proficiency can only
    // NARROW the pre-selection to configured modalities, never introduce one
    // the cohort lacks. May be empty (CM then picks from the shown options); a
    // configured-but-non-proficient modality is still shown, just not
    // pre-selected, with the existing proficiency warning.
    return (commonModalities as ModalityType[]).filter(m => configured.includes(m));
  };

  // Reset state when modal opens
  useEffect(() => {
    if (opened) {
      setStep(1);
      setSelectedModalities(getInitialModalities());
      // Default deadline to 1 week from today
      const oneWeekFromNow = new Date();
      oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
      setResponseDeadline(oneWeekFromNow);
      setCohortContext('');
      setEmailSubject(DEFAULT_EMAIL_SUBJECT);
      setEmailMessage(DEFAULT_EMAIL_MESSAGE);

      // MED-918 AC17: snapshot the open-time selection (IDs + display names)
      // so we can detect coaches removed *since the modal opened*. We
      // capture names now because a coach who later gets removed drops out
      // of `coaches`, and we'd lose the ability to render their name in
      // the warning.
      const namesByUserId: Record<string, string> = {};
      for (const userId of selectedCoachUserIds) {
        const coach = coaches.find(c => c.user_id === userId);
        if (coach) {
          const fullName = [coach.first_name, coach.last_name].filter(Boolean).join(' ');
          namesByUserId[userId] = fullName || coach.email || userId;
        } else {
          // Already missing at open — fall back to the raw ID for display.
          namesByUserId[userId] = userId;
        }
      }
      setOpenTimeSelectionSnapshot({
        userIds: [...selectedCoachUserIds],
        namesByUserId,
      });
    } else {
      // Clear snapshot on close so a future re-open starts fresh.
      setOpenTimeSelectionSnapshot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally snapshot only on open-transition; later changes to selectedCoachUserIds/coaches drive the drift comparison, not the snapshot itself
  }, [opened]);

  // MED-918 AC17: compute which open-time-selected coaches are no longer in
  // the live `coaches` array. Their names come from the snapshot taken at
  // modal-open since the coach itself has been dropped from `coaches`.
  const droppedSinceOpen = openTimeSelectionSnapshot
    ? openTimeSelectionSnapshot.userIds
        .filter(id => !coaches.some(c => c.user_id === id))
        .map(id => openTimeSelectionSnapshot.namesByUserId[id] ?? id)
    : [];

  // Toggle modality selection
  const toggleModality = (modality: ModalityType) => {
    setSelectedModalities(prev =>
      prev.includes(modality) ? prev.filter(m => m !== modality) : [...prev, modality],
    );
  };

  // Toggle time slot selection
  const toggleTimeSlot = (timeSlotId: string) => {
    setSelectedTimeSlotIds(prev =>
      prev.includes(timeSlotId) ? prev.filter(id => id !== timeSlotId) : [...prev, timeSlotId],
    );
  };

  // Get coach names for display
  const getCoachNames = () => {
    return coaches
      .map(c => [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email)
      .join(', ');
  };

  // Format time slot for display
  const formatTimeSlot = (slot: TimeSlotResponse) => {
    // Use display values from the response (already formatted by API)
    return `${slot.display_day}s - ${slot.display_time}`;
  };

  // Parse date-only strings (YYYY-MM-DD) as local midnight to avoid UTC
  // interpretation shifting the displayed date back one day. Mirrors the
  // backend production send path (CohortCoachController.sendAvailabilityRequest)
  // so the preview faithfully represents the outgoing email (MED-915).
  const parseDateLocal = (date: Date | string | null): Date | null => {
    if (!date) return null;
    if (date instanceof Date) return date;
    const str = String(date);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
      const [year, month, day] = str.slice(0, 10).split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(str);
  };

  const formatPreviewDate = (date: Date | string | null): string => {
    const d = parseDateLocal(date);
    if (!d) return 'TBD';
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatPreviewDeadline = (date: Date | string | null): string => {
    const d = parseDateLocal(date);
    // Mirror the production send fallback so the preview matches a real send.
    if (!d) return 'as soon as possible';
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  // Generate preview email
  const generatePreview = () => {
    let preview = emailMessage;
    preview = preview.replace(/\{\{coach_name\}\}/g, coaches[0]?.first_name || 'Coach');
    preview = preview.replace(/\{\{start_date\}\}/g, formatPreviewDate(cohort.start_date));
    preview = preview.replace(/\{\{end_date\}\}/g, formatPreviewDate(cohort.end_date));
    preview = preview.replace(/\{\{cohort_context\}\}/g, cohortContext || '[Cohort context will appear here]');
    preview = preview.replace(/\{\{modalities\}\}/g, selectedModalities.join(', '));
    preview = preview.replace(
      /\{\{deadline\}\}/g,
      formatPreviewDeadline(responseDeadline),
    );
    preview = preview.replace(/\{\{cm_name\}\}/g, 'Sarah Mitchell'); // TODO: Use actual user name
    preview = preview.replace(/\{\{cohort_name\}\}/g, cohort.name);
    preview = preview.replace(/\{\{availability_link\}\}/g, 'https://medley.com/availability/abc123');
    return preview;
  };

  // Submit availability request
  const handleSubmit = async () => {
    // MDF-233: a reminder-only send restates nothing, so it needs no
    // modalities and no time slots. Everything else still does.
    if (!isReminderOnly && (selectedModalities.length === 0 || selectedTimeSlotIds.length === 0)) {
      notifications.show({
        title: 'Missing Information',
        message: 'Please select at least one modality and time slot',
        color: 'red',
      });
      return;
    }

    try {
      setSubmitting(true);
      const payload: SendAvailabilityRequestPayload = isReminderOnly
        ? {
            coach_user_ids: selectedCoachUserIds,
            email_subject: emailSubject,
            email_message: emailMessage,
          }
        : {
            coach_user_ids: selectedCoachUserIds,
            modalities: selectedModalities,
            time_slot_ids: selectedTimeSlotIds,
            response_deadline: responseDeadline?.toISOString(),
            cohort_context: cohortContext,
            email_subject: emailSubject,
            email_message: emailMessage,
            on_existing: onExisting,
          };
      const result = await cohortCoachesApi.sendAvailabilityRequest(cohort.id, payload);

      // MDF-233: report what actually happened. "Sent to 5 coaches" hides the
      // difference between a reminder and a new request, and that difference
      // is the whole point of this change.
      const parts = [
        result.requested_count ? `${result.requested_count} new request${result.requested_count === 1 ? '' : 's'}` : null,
        result.reminded_count ? `${result.reminded_count} reminder${result.reminded_count === 1 ? '' : 's'}` : null,
        result.resent_count ? `${result.resent_count} replaced` : null,
      ].filter(Boolean);
      notifications.show({
        title: 'Sent',
        message: parts.length > 0 ? `Sent ${parts.join(' and ')}.` : 'Nothing was sent.',
        color: 'green',
      });

      onRequestSent();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to send availability request',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Send test email
  const handleSendTestEmail = async () => {
    try {
      setSendingTest(true);
      await cohortCoachesApi.sendTestEmail(
        cohort.id,
        emailSubject,
        emailMessage,
        responseDeadline?.toISOString(),
      );
      notifications.show({
        title: 'Test Email Sent',
        message: 'A test email has been sent to your email address',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to send test email',
        color: 'red',
      });
    } finally {
      setSendingTest(false);
    }
  };

  // MDF-233: a reminder needs no time slots, because it restates nothing. Only
  // a send that creates a request is blocked when the cohort has none.
  const hasNoTimeSlots = timeSlots.length === 0;

  const handleEditCohortDetails = () => {
    onClose();
    onEditCohortDetails?.();
  };

  // ---------------------------------------------------------------------
  // MDF-233 helpers for the split callout.
  // ---------------------------------------------------------------------

  /** Names for a set of coach user IDs, for copy that a human reads. */
  const coachNamesFor = (userIds: string[]): string =>
    userIds
      .map(id => {
        const coach = coaches.find(c => c.user_id === id);
        if (!coach) return id;
        return [coach.first_name, coach.last_name].filter(Boolean).join(' ') || coach.email || id;
      })
      .join(', ');

  const openRequestCount = preview?.open_requests.length ?? 0;

  const openRequestHeading =
    openRequestCount === 1
      ? '1 coach already has an open request'
      : `${openRequestCount} coaches already have an open request`;

  /** "was asked on Sep 8 about 15 time slots." — the ask they already hold. */
  const oldestOpenRequestSummary = (() => {
    const rows = preview?.open_requests ?? [];
    if (rows.length === 0) return '';
    const oldest = rows.reduce((a, b) =>
      new Date(a.requested_at) <= new Date(b.requested_at) ? a : b,
    );
    const asked = new Date(oldest.requested_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const slotCount = oldest.requested_time_slots.length;
    const verb = rows.length === 1 ? 'was' : 'were';
    return ` ${verb} asked on ${asked} about ${slotCount} time slot${slotCount === 1 ? '' : 's'}.`;
  })();

  /**
   * Whether the form now differs from the ask those coaches already hold.
   * Deadline is deliberately excluded: the modal resets it to a week out every
   * time it opens, so counting it would flag every single send as a change.
   */
  const askHasChanged = (() => {
    const first = preview?.open_requests[0];
    if (!first) return false;
    const sameSlots =
      [...first.requested_time_slots].sort().join('|') === [...selectedTimeSlotIds].sort().join('|');
    const sameModalities =
      [...first.modalities].sort().join('|') === [...selectedModalities].sort().join('|');
    return !(sameSlots && sameModalities);
  })();

  /**
   * Every selected coach already holds an open request and the CM is not
   * replacing anything, so this send restates nothing at all.
   */
  const isReminderOnly =
    !!preview && preview.coaches_without_open_request.length === 0 && onExisting === 'remind';

  const canContinue =
    isReminderOnly || (selectedModalities.length > 0 && selectedTimeSlotIds.length > 0);

  /**
   * Both halves are present, so one coach gets a reminder while another gets a
   * brand new request. This used to render as two stacked callouts that each
   * pointed at the same form with a different scope, so nobody could tell
   * whether editing a time slot reached one group or both.
   */
  const isMixedSend =
    !!preview &&
    preview.open_requests.length > 0 &&
    preview.coaches_without_open_request.length > 0;

  /**
   * How this send splits. The review step states both halves, because the
   * request details below apply only to the coaches who get a new request.
   */
  const willRemindCount = onExisting === 'remind' ? openRequestCount : 0;
  const willRequestCount =
    (preview?.coaches_without_open_request.length ?? selectedCoachUserIds.length) +
    (onExisting === 'replace' ? openRequestCount : 0);

  const sendActionSummary =
    [
      willRemindCount > 0 ? `${willRemindCount} reminder${willRemindCount === 1 ? '' : 's'}` : null,
      willRequestCount > 0
        ? `${willRequestCount} new request${willRequestCount === 1 ? '' : 's'}`
        : null,
    ]
      .filter(Boolean)
      .join(', ') || 'Nothing to send';

  /**
   * What a reminder repeats. A reminder reuses the stored request, so this
   * names that request rather than the form, which the CM never filled in.
   */
  const reminderRepeatsSummary = (() => {
    const rows = preview?.open_requests ?? [];
    if (rows.length === 0) return '';
    const oldest = rows.reduce((a, b) =>
      new Date(a.requested_at) <= new Date(b.requested_at) ? a : b,
    );
    const mixedDates = rows.some(r => r.requested_at !== oldest.requested_at);
    if (mixedDates) return 'each coach\u2019s existing request, unchanged';
    const asked = new Date(oldest.requested_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const slotCount = oldest.requested_time_slots.length;
    return `the request from ${asked}, ${slotCount} time slot${slotCount === 1 ? '' : 's'}`;
  })();


  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fw={600} size="lg">Send Availability Request</Text>
          {/* MDF-233: "Request Details" is a lie in the reminder-only state,
              where the details form is hidden on purpose. */}
          <Text size="sm" c={MEDLEY_TEXT_SECONDARY}>
            Step {step} of 2:{' '}
            {step === 2
              ? 'Review & Send Email'
              : isReminderOnly
                ? 'Choose what to send'
                : 'Request Details'}
          </Text>
        </Stack>
      }
      size="lg"
      styles={{
        body: { padding: 0 },
        header: { padding: '20px 24px', borderBottom: '1px solid var(--mantine-color-gray-2)' },
      }}
    >
      {step === 1 ? (
        // Step 1: Request Details
        <Stack gap={0}>
          <Box p="md">
            <Stack gap="lg">
              {/* MED-918 AC17: inline pre-flight warning when coaches have
                  been removed from the cohort since the modal opened. The
                  warning lists the dropped coach names BEFORE Send so the
                  CM can decide whether to abort and re-select, or proceed
                  with the reduced recipient set. AC16 backend filter still
                  catches any user IDs that slip through here. */}
              {droppedSinceOpen.length > 0 && (
                <Alert
                  icon={<AlertTriangle size={18} />}
                  color="warningAmber"
                  variant="light"
                  title={`${droppedSinceOpen.length} coach${droppedSinceOpen.length === 1 ? ' was' : 'es were'} removed from the cohort`}
                >
                  <Text size="sm">
                    The following will not receive this availability request:{' '}
                    <Text component="span" fw={600}>{droppedSinceOpen.join(', ')}</Text>.
                    The remaining {selectedCoachUserIds.length} coach
                    {selectedCoachUserIds.length === 1 ? '' : 'es'} will still be sent the request,
                    or cancel and re-select if you want to abort.
                  </Text>
                </Alert>
              )}

              {/* MDF-233: the split. Says who gets a reminder and who gets a new
                  request, before anybody clicks Send, and lets the CM choose
                  to replace instead. Reminder is the default because it is the
                  outcome that cannot surprise anyone. */}
              {loadingPreview && (
                <Center py="sm">
                  <Loader size="sm" />
                </Center>
              )}

              {!loadingPreview && preview && preview.open_requests.length > 0 && (
                <Alert
                  color="gray"
                  variant="light"
                  title={isMixedSend ? 'This send does two things' : openRequestHeading}
                >
                  <Stack gap="xs">
                    {isMixedSend && (
                      <Text size="sm" fw={600}>
                        {preview.open_requests.length} already asked
                      </Text>
                    )}
                    <Text size="sm">
                      <Text component="span" fw={600}>
                        {coachNamesFor(preview.open_requests.map(r => r.coach_user_id))}
                      </Text>
                      {oldestOpenRequestSummary}
                    </Text>

                    <Radio.Group
                      value={onExisting}
                      onChange={value => setOnExisting(value as 'remind' | 'replace')}
                    >
                      <Stack gap={6} mt={4}>
                        {/* These two sentences are the whole explanation of
                            remind against replace, so they carry the Medley
                            secondary token rather than Mantine's dimmed
                            #868E96, which fails AA on this background. */}
                        <Radio
                          value="remind"
                          label="Send a reminder"
                          description="Same request, same link. Nothing new appears on their dashboard."
                          styles={{ description: { color: MEDLEY_TEXT_SECONDARY } }}
                        />
                        <Radio
                          value="replace"
                          label="Replace it with a new request"
                          description="Their current request is marked replaced. They keep it on their dashboard for reference, greyed out."
                          styles={{ description: { color: MEDLEY_TEXT_SECONDARY } }}
                        />
                      </Stack>
                    </Radio.Group>

                    {onExisting === 'remind' && askHasChanged && (
                      <Text size="xs" c={MEDLEY_TEXT_SECONDARY} fs="italic">
                        You changed the request details below. Those changes apply only to the
                        coaches who have not been asked yet. Choose Replace if you want them to
                        apply to everyone.
                      </Text>
                    )}

                    {/*
                      The second half of a mixed send lives in the same box as
                      the first, under a heading that names both, so the scope
                      of the form below is stated once instead of contradicted
                      across two separate callouts.
                    */}
                    {isMixedSend && (
                      <>
                        <Divider my={4} />
                        <Text size="sm" fw={600}>
                          {preview.coaches_without_open_request.length} not asked yet
                        </Text>
                        <Text size="sm">
                          <Text component="span" fw={600}>
                            {coachNamesFor(preview.coaches_without_open_request)}
                          </Text>{' '}
                          {preview.coaches_without_open_request.length === 1 ? 'gets' : 'get'} a new
                          request built from the details below.
                        </Text>
                        <Text size="xs" c={MEDLEY_TEXT_SECONDARY}>
                          {onExisting === 'replace'
                            ? 'You chose Replace, so the details below go to everyone selected.'
                            : 'Only the new request uses the details below. The reminder repeats the request already sent, unchanged.'}
                        </Text>
                      </>
                    )}
                  </Stack>
                </Alert>
              )}

              {/* No Time Slots Warning */}
              {hasNoTimeSlots && !isReminderOnly && (
                <Alert
                  icon={<AlertTriangle size={18} />}
                  color="warningAmber"
                  variant="light"
                  title="No time slots configured for this cohort yet"
                >
                  <Text size="sm" mb="sm">
                    Add time slots to request coach availability.
                  </Text>
                  {onEditCohortDetails && (
                    <Button
                      size="xs"
                      variant="outline"
                      color="warningAmber"
                      leftSection={<Edit size={14} />}
                      onClick={handleEditCohortDetails}
                    >
                      Edit Cohort Details
                    </Button>
                  )}
                </Alert>
              )}

              {/* Form fields - disabled when no time slots.
                  MDF-233: hidden entirely for a reminder-only send. There is
                  nothing to fill in, because the coaches keep the request and
                  the link they already have. Choosing Replace above brings the
                  form straight back. */}
              <Box
                hidden={isReminderOnly}
                style={
                  hasNoTimeSlots && !isReminderOnly
                    ? { opacity: 0.5, pointerEvents: 'none' }
                    : undefined
                }
              >
              <Stack gap="lg">
              {/* TO Card */}
              <Paper withBorder p="md" radius="md" bg="gray.0">
                <Stack gap={4}>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                    TO
                  </Text>
                  <Text fw={500}>{selectedCoachUserIds.length} coach{selectedCoachUserIds.length !== 1 ? 'es' : ''} selected</Text>
                  <Text size="sm" c="dimmed" lineClamp={1}>
                    {getCoachNames()}
                  </Text>
                </Stack>
              </Paper>

              {/* Modalities */}
              <Stack gap="xs">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                  MODALITIES TO INCLUDE
                </Text>
                <SimpleGrid cols={Math.min(availableModalityOptions.length, 3) || 1} spacing="md">
                  {availableModalityOptions.map(modality => (
                    <Paper
                      key={modality.value}
                      withBorder
                      p="md"
                      radius="md"
                      style={{
                        cursor: 'pointer',
                        borderColor: selectedModalities.includes(modality.value)
                          ? 'var(--mantine-color-medleyGreen-6)'
                          : undefined,
                        borderWidth: selectedModalities.includes(modality.value) ? 2 : 1,
                      }}
                      onClick={() => toggleModality(modality.value)}
                    >
                      <Stack align="center" gap="xs">
                        <ThemeIcon
                          size={40}
                          radius="md"
                          variant={selectedModalities.includes(modality.value) ? 'filled' : 'light'}
                          color="medleyGreen"
                        >
                          {modality.icon}
                        </ThemeIcon>
                        <Text size="sm" fw={500} ta="center">
                          {modality.label}
                        </Text>
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
                {/* Warning banner for mismatched modalities and approval status */}
                {(() => {
                  const selectedCoaches = coaches.filter(c => selectedCoachUserIds.includes(c.user_id));
                  const proficiencyWarnings: string[] = [];
                  const approvalWarnings: string[] = [];
                  for (const modality of selectedModalities) {
                    const modalityLabel = MODALITY_OPTIONS.find(m => m.value === modality)?.label || modality;
                    const proficiencyMismatch = selectedCoaches.filter(
                      c => !(c.modality_proficiencies || []).includes(modality)
                    ).length;
                    if (proficiencyMismatch > 0) {
                      proficiencyWarnings.push(`${proficiencyMismatch} coach${proficiencyMismatch !== 1 ? 'es do' : ' does'} not have ${modalityLabel} in their profile`);
                    }
                    const approvalMismatch = selectedCoaches.filter(
                      c => !(c.modality_approvals || []).includes(modality)
                    ).length;
                    if (approvalMismatch > 0) {
                      approvalWarnings.push(`${approvalMismatch} coach${approvalMismatch !== 1 ? 'es do' : ' does'} not have ${modalityLabel} approved by Medley`);
                    }
                  }
                  const allWarnings = [...proficiencyWarnings, ...approvalWarnings];
                  return allWarnings.length > 0 ? (
                    <Paper withBorder p="sm" radius="md" bg="warningAmber.0" mt="xs" style={{ borderColor: 'var(--mantine-color-warningAmber-3)' }}>
                      <Stack gap={4}>
                        {allWarnings.map((warning, i) => (
                          <Group key={i} gap="xs" wrap="nowrap">
                            <AlertTriangle size={14} color="var(--mantine-color-warningAmber-6)" />
                            <Text size="xs" c="warningAmber.8">{warning}</Text>
                          </Group>
                        ))}
                        {approvalWarnings.length > 0 && (
                          <Text size="xs" c="warningAmber.8" ml={22} fs="italic">
                            Review coach profiles to update approval status before sending.
                          </Text>
                        )}
                      </Stack>
                    </Paper>
                  ) : null;
                })()}
              </Stack>

              {/* Time Slots */}
              <Stack gap="xs">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                  TIME SLOTS TO INCLUDE
                </Text>
                <Stack gap="xs">
                  {timeSlots.map(slot => (
                    <Paper
                      key={slot.id}
                      withBorder
                      p="sm"
                      radius="md"
                      style={{
                        cursor: 'pointer',
                        borderColor: selectedTimeSlotIds.includes(slot.id)
                          ? 'var(--mantine-color-medleyGreen-6)'
                          : undefined,
                      }}
                      onClick={() => toggleTimeSlot(slot.id)}
                    >
                      <Group>
                        {/*
                          The card below owns the toggle. Without stopPropagation
                          a click on the box toggles twice, once here and once on
                          the card, so the slot never changes. Clicking the card
                          body worked, which is why this stayed hidden.
                        */}
                        <Checkbox
                          checked={selectedTimeSlotIds.includes(slot.id)}
                          onChange={() => toggleTimeSlot(slot.id)}
                          onClick={e => e.stopPropagation()}
                        />
                        <Stack gap={0}>
                          <Text size="sm" fw={500}>
                            {formatTimeSlot(slot)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {slot.timezone}
                          </Text>
                        </Stack>
                      </Group>
                    </Paper>
                  ))}
                  {timeSlots.length === 0 && (
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      No time slots configured for this cohort yet
                    </Text>
                  )}
                </Stack>
              </Stack>

              {/* MED-663: Per-coach compensation estimates (read-only) */}
              {compData && selectedCoachUserIds.length > 0 && (
                <Stack gap="xs">
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                    Compensation Estimates
                  </Text>
                  <Paper withBorder p="sm" radius="md" bg="gray.0">
                    <Stack gap="xs">
                      {selectedCoachUserIds.map(userId => {
                        const coachResult = [...(compData.coaches ?? []), ...(compData.pending_coaches ?? [])].find(
                          c => c.coach_user_id === userId,
                        );
                        const coachInfo = coaches.find(c => c.user_id === userId);
                        const coachName = coachInfo
                          ? [coachInfo.first_name, coachInfo.last_name].filter(Boolean).join(' ')
                          : 'Unknown';
                        const overrideCents = coachResult?.compensation_override_cents;
                        const estimatedCents = coachResult?.estimated_total_cents;
                        const displayCents = overrideCents ?? estimatedCents;
                        const isEstimate = coachResult?.estimation_basis === 'pre_assignment';

                        return (
                          <Group key={userId} justify="space-between">
                            <Text size="sm">{coachName}</Text>
                            <Group gap="xs">
                              {displayCents != null ? (
                                <>
                                  <Text size="sm" fw={600}>
                                    {formatCents(displayCents)}
                                  </Text>
                                  {isEstimate && (
                                    <Badge size="xs" variant="light" color="blue">
                                      Est.
                                    </Badge>
                                  )}
                                  {overrideCents != null && (
                                    <Badge size="xs" variant="light" color="orange">
                                      Override
                                    </Badge>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">Pending</Text>
                              )}
                            </Group>
                          </Group>
                        );
                      })}
                    </Stack>
                  </Paper>
                  <Text size="xs" c="dimmed" fs="italic">
                    <DollarSign size={10} style={{ display: 'inline', verticalAlign: 'middle' }} />{' '}
                    To adjust a coach&apos;s compensation estimate before sending, update it in the Compensation tab coach list.
                  </Text>
                </Stack>
              )}

              {/* Deadline */}
              <DateInput
                label="RESPONSE DEADLINE"
                value={responseDeadline}
                onChange={value => setResponseDeadline(toDateValue(value))}
                placeholder="Select date"
                valueFormat="MM/DD/YYYY"
                styles={{ label: { fontSize: 10, fontWeight: 600, color: 'var(--mantine-color-dimmed)', textTransform: 'uppercase' } }}
              />

              {/* Cohort Context */}
              <Textarea
                label="COHORT CONTEXT"
                description="This context will be inserted into the email sent to coaches."
                value={cohortContext}
                onChange={e => setCohortContext(e.target.value)}
                placeholder="e.g., This engagement is a follow-on from a workshop we recently hosted focused on career visioning for a group of finance professionals."
                minRows={3}
                styles={{
                  label: { fontSize: 10, fontWeight: 600, color: 'var(--mantine-color-dimmed)', textTransform: 'uppercase' },
                  description: { marginTop: 2, marginBottom: 6 },
                }}
              />

              {/* Cohort Timeline */}
              <CohortTimeline
                events={timelineEvents}
                totalEvents={timelineTotalEvents}
                defaultExpanded={false}
              />
            </Stack>
            </Box>
            </Stack>
          </Box>

          {/*
            Footer. Sticky: choosing Replace grows the body by about 1400px, and
            an unpinned footer took the only primary action off screen. The
            state line keeps the chosen action next to the button that commits
            it, because the radio pair scrolls away long before this point.
          */}
          <Box
            p="md"
            style={{
              borderTop: '1px solid #E8E6E1',
              position: 'sticky',
              bottom: 0,
              background: '#FFFFFF',
              zIndex: 1,
            }}
          >
            <Group justify="space-between">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Group gap="md">
                {preview && (
                  <Text
                    size="sm"
                    fw={onExisting === 'replace' && openRequestCount > 0 ? 600 : 400}
                    c={
                      onExisting === 'replace' && openRequestCount > 0
                        ? MEDLEY_WARNING_TEXT
                        : MEDLEY_TEXT_SECONDARY
                    }
                  >
                    {onExisting === 'replace' && openRequestCount > 0
                      ? `Replacing ${openRequestCount} existing request${openRequestCount === 1 ? '' : 's'}`
                      : sendActionSummary}
                  </Text>
                )}
              <Button
                color="medleyGreen"
                onClick={() => setStep(2)}
                disabled={!canContinue || (hasNoTimeSlots && !isReminderOnly)}
                rightSection={<ArrowLeft size={16} style={{ transform: 'rotate(180deg)' }} />}
              >
                Continue to Email
              </Button>
              </Group>
            </Group>
          </Box>
        </Stack>
      ) : (
        // Step 2: Review & Send Email
        <Stack gap={0}>
          <Box p="md">
            <Stack gap="lg">
              {/* MED-918 AC17: same inline pre-flight warning surfaces here
                  too so a CM who advanced to Step 2 still sees it right
                  above the final Send button. */}
              {droppedSinceOpen.length > 0 && (
                <Alert
                  icon={<AlertTriangle size={18} />}
                  color="warningAmber"
                  variant="light"
                  title={`${droppedSinceOpen.length} coach${droppedSinceOpen.length === 1 ? ' was' : 'es were'} removed from the cohort`}
                >
                  <Text size="sm">
                    The following will not receive this availability request:{' '}
                    <Text component="span" fw={600}>{droppedSinceOpen.join(', ')}</Text>.
                    The remaining {selectedCoachUserIds.length} coach
                    {selectedCoachUserIds.length === 1 ? '' : 'es'} will still be sent the request,
                    or cancel and re-select if you want to abort.
                  </Text>
                </Alert>
              )}

              {/* Summary Card */}
              <Paper withBorder p="md" radius="md" bg="gray.0">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
                  REQUEST SUMMARY
                </Text>
                <SimpleGrid cols={2} spacing="xs">
                  <Text size="sm">Recipients:</Text>
                  <Text size="sm" fw={500}>{selectedCoachUserIds.length} coaches</Text>
                  <Text size="sm">Action:</Text>
                  <Text size="sm" fw={500}>{sendActionSummary}</Text>
                  {/*
                    MDF-233: "2 new requests" is true and it hides the half that
                    cannot be undone. The last screen before Send has to say it.
                  */}
                  {onExisting === 'replace' && openRequestCount > 0 && (
                    <>
                      <Text size="sm">Replacing:</Text>
                      <Text size="sm" fw={600} c={MEDLEY_WARNING_TEXT}>
                        {openRequestCount} existing request
                        {openRequestCount === 1 ? '' : 's'}, marked replaced
                      </Text>
                    </>
                  )}
                  {willRemindCount > 0 && (
                    <>
                      <Text size="sm">Reminder repeats:</Text>
                      <Text size="sm" fw={500}>{reminderRepeatsSummary}</Text>
                    </>
                  )}
                  {willRequestCount > 0 && (
                    <>
                      <Text size="sm">{willRemindCount > 0 ? 'New request modalities:' : 'Modalities:'}</Text>
                      <Text size="sm" fw={500}>{selectedModalities.join(', ')}</Text>
                      <Text size="sm">{willRemindCount > 0 ? 'New request time slots:' : 'Time Slots:'}</Text>
                      <Text size="sm" fw={500}>{selectedTimeSlotIds.length} selected</Text>
                      <Text size="sm">{willRemindCount > 0 ? 'New request deadline:' : 'Deadline:'}</Text>
                      <Text size="sm" fw={500}>
                        {responseDeadline ? responseDeadline.toLocaleDateString() : 'Not set'}
                      </Text>
                    </>
                  )}
                </SimpleGrid>
              </Paper>

              {/* Email Subject */}
              <TextInput
                ref={subjectField.ref}
                label="SUBJECT"
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
                onFocus={() => setLastFocusedField('subject')}
                onSelect={subjectField.trackCursor}
                styles={{ label: { fontSize: 10, fontWeight: 600, color: 'var(--mantine-color-dimmed)', textTransform: 'uppercase' } }}
              />

              {/* Merge Tags */}
              <MergeTagChips tags={MERGE_TAGS} onInsert={handleInsertTag} />

              {/* Message */}
              <Textarea
                ref={bodyField.ref}
                label="MESSAGE"
                value={emailMessage}
                onChange={e => setEmailMessage(e.target.value)}
                onFocus={() => setLastFocusedField('body')}
                onSelect={bodyField.trackCursor}
                minRows={8}
                autosize
                maxRows={16}
                styles={{ label: { fontSize: 10, fontWeight: 600, color: 'var(--mantine-color-dimmed)', textTransform: 'uppercase' } }}
              />

              {/* Preview What Coach Will Receive */}
              <Accordion defaultValue="preview-request">
                <Accordion.Item value="preview-request">
                  <Accordion.Control>
                    Preview What Coach Will Receive
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      {/* Cohort Summary */}
                      <Paper withBorder p="md" radius="md">
                        <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
                          Cohort Summary
                        </Text>
                        <SimpleGrid cols={2} spacing="xs">
                          <Text size="sm">Cohort:</Text>
                          <Text size="sm" fw={500}>{cohort.name}</Text>
                          <Text size="sm">Dates:</Text>
                          <Text size="sm" fw={500}>
                            {formatDateOnlyRange(cohort.start_date, cohort.end_date)}
                          </Text>
                        </SimpleGrid>
                      </Paper>

                      {/*
                        MDF-233: a reminder offers no new time slots. It repeats
                        the request each coach already holds, so listing the form
                        here would name slots this send never carries.
                      */}
                      <Paper withBorder p="md" radius="md">
                        <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
                          {isReminderOnly ? 'Time Slots' : 'Time Slots Offered'}
                        </Text>
                        {isReminderOnly ? (
                          <Text size="sm" c="dimmed">
                            A reminder repeats {reminderRepeatsSummary}. The time slots do not
                            change, and the coach keeps the same link.
                          </Text>
                        ) : (
                          <Stack gap="xs">
                            {timeSlots
                              .filter(ts => selectedTimeSlotIds.includes(ts.id))
                              .map(slot => (
                                <Text key={slot.id} size="sm">
                                  {formatTimeSlot(slot)}
                                </Text>
                              ))}
                          </Stack>
                        )}
                      </Paper>

                      {/* Timeline Preview */}
                      {timelineEvents.length > 0 && (
                        <CohortTimeline
                          events={timelineEvents}
                          totalEvents={timelineTotalEvents}
                          defaultExpanded={true}
                        />
                      )}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>

              {/* Preview Email */}
              <Accordion>
                <Accordion.Item value="preview">
                  <Accordion.Control icon={<Mail size={16} />}>
                    Preview Email
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Paper withBorder p="md" radius="md" bg="gray.0">
                      <Stack gap="xs">
                        <Text size="sm" fw={500}>Subject:</Text>
                        <Text size="sm">{emailSubject.replace(/\{\{cohort_name\}\}/g, cohort.name)}</Text>
                        <Divider my="xs" />
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                          {generatePreview()}
                        </Text>
                      </Stack>
                    </Paper>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Stack>
          </Box>

          {/* Footer */}
          <Box p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
            <Group justify="space-between">
              <Button
                variant="outline"
                leftSection={<ArrowLeft size={16} />}
                onClick={() => setStep(1)}
              >
                Back
              </Button>
              <Group gap="xs">
                <Button
                  variant="outline"
                  leftSection={<Mail size={16} />}
                  onClick={handleSendTestEmail}
                  loading={sendingTest}
                >
                  Send Test Email
                </Button>
                <Button
                  color="medleyGreen"
                  leftSection={<Send size={16} />}
                  onClick={() => setConfirmOpened(true)}
                  loading={submitting}
                >
                  {isReminderOnly
                    ? `Send ${willRemindCount} Reminder${willRemindCount === 1 ? '' : 's'}`
                    : `Send to ${selectedCoachUserIds.length} Coach${selectedCoachUserIds.length !== 1 ? 'es' : ''}`}
                </Button>
              </Group>
            </Group>
          </Box>
        </Stack>
      )}

      {/* MED-646: Bulk send confirmation dialog */}
      <Modal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        title="Confirm Email Send"
        size="sm"
        centered
        zIndex={301}
        // The global Modal theme floors every dialog at 900px, so this one
        // rendered the same width, the same x and the same radius as the
        // modal behind it and read as a panel inside it rather than as a
        // confirmation. size="sm" is honoured here only.
        // The shadow goes here rather than on the shadow prop, which the
        // theme's own content styles were overriding to none.
        styles={{
          content: {
            minWidth: 'min(480px, calc(100vw - 32px))',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            {/*
              MDF-233: name the action, not just the recipient count. A reminder
              and a replacement are very different, and the replacement is the
              one that changes what the coach already holds.
            */}
            <Text size="sm">
              You are about to send{' '}
              <Text component="span" fw={700}>
                {sendActionSummary}
              </Text>{' '}
              to {selectedCoachUserIds.length} coach
              {selectedCoachUserIds.length !== 1 ? 'es' : ''}. This action cannot be undone.
            </Text>
            {isReminderOnly && (
              <Text size="sm" mt="xs">
                Nothing new appears on their dashboard. Each coach gets the request they
                already hold, with the same link.
              </Text>
            )}
            {onExisting === 'replace' && openRequestCount > 0 && (
              <Text size="sm" mt="xs">
                {openRequestCount} existing request{openRequestCount === 1 ? '' : 's'} will be
                marked replaced. Each coach keeps the old request on the dashboard for
                reference, greyed out, and can no longer answer it.
              </Text>
            )}
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setConfirmOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            {/*
              Danger, not primary. The dialog's own text says the action cannot
              be undone, and the parent modal's green Send button stays visible
              behind this one.
            */}
            <Button
              // Shade pinned: bare dangerRed resolves to #991B1B, not the
              // Medley Danger Red token at shade 5.
              color="dangerRed.5"
              leftSection={<Send size={14} />}
              onClick={() => {
                setConfirmOpened(false);
                handleSubmit();
              }}
              loading={submitting}
              disabled={submitting}
            >
              Confirm &amp; Send
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}

export default SendAvailabilityRequestModal;
